/**
 * Historial deshacer/rehacer para movimiento de nodos en el mapa (y rutas de cables asociadas).
 * @module stores/useMapHistoryStore
 */

import { create } from 'zustand'
import type { PuntoRuta } from '@/types/fibers'
import { useNodeStore } from './useNodeStore'
import { useFiberStore } from './useFiberStore'

const MAX_DEPTH = 50

export interface NodeMoveHistoryEntry {
  nodeId: string
  before: { lat: number; lng: number }
  after: { lat: number; lng: number }
  cables: Array<{ id: string; beforeRuta: PuntoRuta[]; afterRuta: PuntoRuta[] }>
}

function cloneRuta(r: PuntoRuta[] | undefined): PuntoRuta[] {
  if (!r || !Array.isArray(r)) return []
  return JSON.parse(JSON.stringify(r)) as PuntoRuta[]
}

interface MapHistoryStore {
  undoStack: NodeMoveHistoryEntry[]
  redoStack: NodeMoveHistoryEntry[]
  pushNodeMove: (entry: NodeMoveHistoryEntry) => void
  undo: () => Promise<boolean>
  redo: () => Promise<boolean>
}

export const useMapHistoryStore = create<MapHistoryStore>((set, get) => ({
  undoStack: [],
  redoStack: [],

  pushNodeMove: (entry) => {
    set((s) => ({
      undoStack: [...s.undoStack, entry].slice(-MAX_DEPTH),
      redoStack: [],
    }))
  },

  undo: async () => {
    const { undoStack, redoStack } = get()
    if (undoStack.length === 0) return false
    const entry = undoStack[undoStack.length - 1]
    const newUndo = undoStack.slice(0, -1)

    const node = useNodeStore.getState().getNodeById(entry.nodeId)
    if (!node) {
      set({ undoStack: newUndo })
      return false
    }

    try {
      await useNodeStore.getState().updateNode({
        ...node,
        lat: entry.before.lat,
        lng: entry.before.lng,
      } as any)

      for (const c of entry.cables) {
        const cable = useFiberStore.getState().cables.find((x) => x.id === c.id)
        if (cable) {
          await useFiberStore.getState().updateCable({
            ...cable,
            ruta: cloneRuta(c.beforeRuta),
          } as any)
        }
      }

      set({
        undoStack: newUndo,
        redoStack: [...redoStack, entry].slice(-MAX_DEPTH),
      })
      return true
    } catch {
      return false
    }
  },

  redo: async () => {
    const { undoStack, redoStack } = get()
    if (redoStack.length === 0) return false
    const entry = redoStack[redoStack.length - 1]
    const newRedo = redoStack.slice(0, -1)

    const node = useNodeStore.getState().getNodeById(entry.nodeId)
    if (!node) {
      set({ redoStack: newRedo })
      return false
    }

    try {
      await useNodeStore.getState().updateNode({
        ...node,
        lat: entry.after.lat,
        lng: entry.after.lng,
      } as any)

      for (const c of entry.cables) {
        const cable = useFiberStore.getState().cables.find((x) => x.id === c.id)
        if (cable) {
          await useFiberStore.getState().updateCable({
            ...cable,
            ruta: cloneRuta(c.afterRuta),
          } as any)
        }
      }

      set({
        undoStack: [...undoStack, entry].slice(-MAX_DEPTH),
        redoStack: newRedo,
      })
      return true
    } catch {
      return false
    }
  },
}))
